import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import Heading from 'components/common/Heading';
import CoinsRefreshIcon from 'components/icons/illustrative/Coins';
import CupcakeRefreshIcon from 'components/icons/illustrative/Cupcake';
import CuttingBoardRefreshIcon from 'components/icons/illustrative/CuttingBoard';
import DumbbellRefreshIcon from 'components/icons/illustrative/Dumbbell';
import EnjoyRefreshIcon from 'components/icons/illustrative/Enjoy';
import FastStopwatchRefreshIcon from 'components/icons/illustrative/FastStopwatch';
import LightningRefreshIcon from 'components/icons/illustrative/Lightning';
import MicrowaveRefreshIcon from 'components/icons/illustrative/Microwave';
import MountainRefreshIcon from 'components/icons/illustrative/Mountain';
import OvenRefreshIcon from 'components/icons/illustrative/Oven';
import PencilRefreshIcon from 'components/icons/illustrative/Pencil';
import PizzaRefreshIcon from 'components/icons/illustrative/Pizza';
import PortionRefreshIcon from 'components/icons/illustrative/Portion';
import RecycleRefreshIcon from 'components/icons/illustrative/Recycle';
import SaladRefreshIcon from 'components/icons/illustrative/Salad';
import TrophyRefreshIcon from 'components/icons/illustrative/Trophy';
import BalancedMealsIcon from 'components/icons/signUp/BalancedMeals';
import CulinaryCollectionIcon from 'components/icons/signUp/CulinaryCollection';
import DietVarietyIcon from 'components/icons/signUp/DietVariety';
import ExpressIcon from 'components/icons/signUp/Express';
import ExtrasIcon from 'components/icons/signUp/Extras';
import FastAndFreshIcon from 'components/icons/signUp/FastAndFresh';
import HeartIcon from 'components/icons/signUp/Heart';
import MealKitsIcon from 'components/icons/signUp/MealKits';
import MoneyIcon from 'components/icons/signUp/Money';
import NutritionGoalsIcon from 'components/icons/signUp/NutritionGoals';
import OvenReadyIcon from 'components/icons/signUp/OvenReady';
import PencilIcon from 'components/icons/signUp/Pencil';
import PizzaIcon from 'components/icons/signUp/Pizza';
import PortionControlIcon from 'components/icons/signUp/PortionControl';
import ProteinPackedIcon from 'components/icons/signUp/ProteinPacked';
import RecyclingIcon from 'components/icons/signUp/Recycling';
import SaveTimeIcon from 'components/icons/signUp/SaveTime';
import { ProgressBarContext, UserContext, VoucherContext } from 'components/signUpFunnel/Contexts';
import { BrandContext, TEMPO } from 'context/BrandContext';
import localStorage from 'helpers/localStorage';
import useAmexBCE from 'hooks/useAmexBCE';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useBrand from 'hooks/useBrand';
import usePartnershipQuery from 'hooks/usePartnershipQuery';
import useQuery from 'hooks/useQuery';
import useUsaa from 'hooks/useUsaa';
import useWelcomeQuery from 'hooks/useWelcomeQuery';
import {
  STANDARD,
  FAMILY,
  SHOW_PROMO_BANNER_LOCAL_STORAGE,
  PARTNERSHIP_RESOURCE_TYPES,
  HEROES_REFERRER,
} from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import { createEvents, getFunnelMenu } from 'services/RequestService/endpoints/signUp';

import HomeChefHeader from './HomeChefHeader';
import IntroContainerSkeleton from './IntroContainerSkeleton';
import IntroInformationForm from './IntroInformationForm';
import IntroMenuSlideout from './IntroMenuSlideout';
import Questions from './Questions';
import SignInButtonSection from './SignInButtonSection';
import TempoHeader from './TempoHeader';
import UsaaLegal from './UsaaLegal';

const Icons = {
  // Original Intro Page Icons
  BalancedMealsIcon,
  CulinaryCollectionIcon,
  DietVarietyIcon,
  ExpressIcon,
  ExtrasIcon,
  FastAndFreshIcon,
  HeartIcon,
  MealKitsIcon,
  MoneyIcon,
  NutritionGoalsIcon,
  OvenReadyIcon,
  PencilIcon,
  PizzaIcon,
  PortionControlIcon,
  ProteinPackedIcon,
  RecyclingIcon,
  SaveTimeIcon,

  // Funnel UI Refresh Icons

  // Standard Plan
  CupcakeRefreshIcon,
  CuttingBoardRefreshIcon,
  LightningRefreshIcon,
  MicrowaveRefreshIcon,
  OvenRefreshIcon,
  TrophyRefreshIcon,

  // Family Plan
  CoinsRefreshIcon,
  FastStopwatchRefreshIcon,
  RecycleRefreshIcon,
  PencilRefreshIcon,
  PizzaRefreshIcon,

  // Tempo Plan
  DumbbellRefreshIcon,
  MountainRefreshIcon,
  PortionRefreshIcon,
  SaladRefreshIcon,

  // Shared
  EnjoyRefreshIcon, // Used by Family and Tempo
};

const IntroContainer = () => {
  const isAmexUser = useAmexBCE();
  const { brand } = useContext(BrandContext);
  const { setUsaaMemberId } = useContext(VoucherContext);
  const { setProgress } = useContext(ProgressBarContext);
  const { user } = useContext(UserContext);
  const { voucher } = useContext(VoucherContext);
  const isTempo = useBrand(TEMPO);
  const isUsaa = useUsaa();
  const [partnerReferrer, setPartnerReferrer] = useState(null);
  const [mealPlanPreference, setMealPlanPreference] = useState(STANDARD);
  const [menuMeals, setMenuMeals] = useState({});
  const [welcomeData, setWelcomeData] = useState({
    visibleMealKitTraits: [],
    userSignedIn: false,
  });
  const [showMenuSlideout, setShowMenuSlideout] = useState(false);
  const { isQueryParamTrue: hasKrogerParam } = useQuery('kroger', 'true');
  const { isQueryParamTrue: hasMenuParam } = useQuery('menu', 'true');
  const { isQueryParamTrue: isAdSource } = useQuery('source', 'ad');
  const { queryParams } = useQuery();
  const { isPending: isLoadingPartnerData, data: partnershipData } =
    usePartnershipQuery(partnerReferrer);
  const { mealPlanSlug } = useParams();
  const { visibleMealKitTraits } = welcomeData;
  const weeklyMeals = queryParams.get('meals');
  const servings = queryParams.get('servings');
  const isFamilyLead = mealPlanPreference === FAMILY;
  const partnershipLogo = partnershipData?.partnership?.resources?.find(
    resource => resource.resourceType === PARTNERSHIP_RESOURCE_TYPES.LOGO
  )?.url;
  const showMenuReveal = hasMenuParam || localStorage.read('menu_reveal');

  const { isQueryParamTrue: isFromHeroesPage } = useQuery('heroes', 'true');
  isFromHeroesPage && localStorage.write(HEROES_REFERRER, true);

  useEffect(() => {
    if (hasMenuParam || showMenuReveal) {
      localStorage.write('menu_reveal', true);
      const fetchMenu = async () => {
        const funnelMenu = await getFunnelMenu({
          params: { mealPlan: mealPlanSlug, menu: hasMenuParam },
        });
        setMenuMeals(funnelMenu.meals);
      };
      fetchMenu();
    }
  }, [hasMenuParam, showMenuReveal, mealPlanSlug]);

  useEffect(() => {
    if (mealPlanSlug === TEMPO || isTempo) {
      setMealPlanPreference(TEMPO);
    } else if (mealPlanSlug === STANDARD) {
      setMealPlanPreference(STANDARD);
    } else if (mealPlanSlug === FAMILY) {
      setMealPlanPreference(FAMILY);
    } else {
      setMealPlanPreference(STANDARD);
    }
  }, [mealPlanSlug, setMealPlanPreference, isTempo]);

  const selectedTraitsFor = useCallback(
    initialTraits =>
      initialTraits.map(trait => ({
        ...trait,
        selected:
          user?.mealKitTraits?.some(selectedTrait => selectedTrait.name === trait.friendlyName) ||
          false,
      })),
    [user]
  );

  const onSignInClick = useAnalyticsCallback(
    null,
    ({ events, logEvent }) => {
      if (brand) {
        logEvent(events.signUp[brand].intro.signInClicked);
      }
    },
    [brand]
  );

  const {
    isPending: isLoadingWelcomeData,
    isError: isWelcomeError,
    error: welcomeError,
    data: response,
  } = useWelcomeQuery(mealPlanSlug);

  useAnalyticsEffect(
    ({ events, logEvent }) => {
      if (brand && mealPlanSlug && isLoadingWelcomeData === false) {
        logEvent(events.signUp[brand].intro.viewed, {
          mealPlanSlug,
        });
      }
    },
    [brand, mealPlanSlug, isLoadingWelcomeData]
  );

  useEffect(() => {
    if (response?.user) {
      if (response?.user.partnerReferrer) {
        setPartnerReferrer(response.user.partnerReferrer);
      }

      setWelcomeData(prevWelcomeData => ({
        ...prevWelcomeData,
        visibleMealKitTraits: selectedTraitsFor(
          response.user.mealKitTraits?.visibleMealKitTraits
        ).map(trait => ({ ...trait, icon: Icons[trait.icon] || null })),
        userSignedIn: response.user.signedIn,
      }));
      if (response?.user.usaaMemberId) {
        setUsaaMemberId(response.user.usaaMemberId);
      }
    } else if (isWelcomeError) {
      return ErrorLogging.captureException(welcomeError);
    }
  }, [response, selectedTraitsFor, mealPlanSlug]);

  useEffect(() => {
    setProgress({ percent: '15' });
    createEvents({ params: { event_name: 'PageView' } });
  }, []);

  useEffect(() => {
    if (voucher?.showPromoBanner) {
      localStorage.write(SHOW_PROMO_BANNER_LOCAL_STORAGE, true);
    }
  }, [voucher]);

  const handleMealKitTraitClick = useAnalyticsCallback(
    ({ friendlyName, selected }) => {
      const planTraits = ['Just me', 'Two of us', 'A group or family'];
      const isPlanTrait = planTraits.includes(friendlyName);

      const newVisibleMealKitState = visibleMealKitTraits.map(trait => {
        if (isPlanTrait) {
          if (trait.friendlyName === friendlyName) {
            return { ...trait, selected: !selected };
          } else if (planTraits.includes(trait.friendlyName)) {
            return { ...trait, selected: false };
          }
        } else {
          if (trait.friendlyName === friendlyName) {
            return { ...trait, selected: !selected };
          }
        }
        return trait;
      });

      if (newVisibleMealKitState)
        setWelcomeData(prevWelcomeData => ({
          ...prevWelcomeData,
          visibleMealKitTraits: newVisibleMealKitState,
        }));
    },
    ({ logEvent, events, callbackArgs }) => {
      if (!callbackArgs[0].selected) {
        if (brand) {
          logEvent(events.signUp[brand].intro.mealKitTraitClicked, {
            mealKitTraitName: callbackArgs[0].name,
          });
        }
      }
    },
    [visibleMealKitTraits, brand]
  );

  const waitingForData = useMemo(
    () => isLoadingWelcomeData || (!!partnerReferrer && isLoadingPartnerData),
    [isLoadingWelcomeData, isLoadingPartnerData, partnerReferrer]
  );

  const showPartnershipOrKrogerSection = (partnershipData?.ok && partnershipLogo) || hasKrogerParam;
  const showPartnershipLogo = partnershipData && partnershipLogo && !hasKrogerParam;
  const showIntroHeading = !isTempo || !isAdSource;

  return waitingForData ? (
    <IntroContainerSkeleton />
  ) : (
    <>
      {showPartnershipOrKrogerSection && (
        <div className="bg-white-100 text-center">
          {showPartnershipLogo && (
            <img
              style={{ maxWidth: '350px' }}
              className="py-3"
              src={partnershipLogo}
              alt={`${partnershipData?.partnership.name} logo`}
            />
          )}
        </div>
      )}
      {isTempo ? (
        <TempoHeader />
      ) : (
        (!partnershipData || !partnershipData.ok) && <HomeChefHeader isFamilyLead={isFamilyLead} />
      )}

      <div className={'relative mx-auto w-full max-w-2xl px-6 bp1:z-10 bp2:p-0'}>
        {showIntroHeading && (
          <div className={'my-6 max-w-2xl text-left'}>
            <Heading brandStylesOverrides="h1" className="block">
              Let&apos;s get to know you!
            </Heading>
          </div>
        )}
        {visibleMealKitTraits.length > 0 && (
          <Questions
            visibleMealKitTraits={visibleMealKitTraits}
            isFamilyLead={isFamilyLead}
            handleMealKitTraitClick={handleMealKitTraitClick}
          />
        )}

        {showMenuReveal && (
          <>
            <button
              type="button"
              className={`textMd mt-4 w-full border-none p-0 text-center underline ${isTempo ? 'text-tempo-navy-100' : 'text-green-100'}`}
              onClick={() => setShowMenuSlideout(true)}
            >
              Take a look at the upcoming menu!
            </button>
            <IntroMenuSlideout
              showMenuSlideout={showMenuSlideout}
              setShowMenuSlideout={setShowMenuSlideout}
              menuMeals={menuMeals}
              visibleMealKitTraits={visibleMealKitTraits}
            />
          </>
        )}

        <IntroInformationForm
          mealPlanPreference={mealPlanPreference}
          selectedMealKitTraits={visibleMealKitTraits
            .filter(trait => trait.selected)
            .map(trait => trait.friendlyName)}
          isAmexUser={isAmexUser}
          brand={brand}
          weeklyMeals={weeklyMeals}
          servings={servings}
          hidePlanChangePage={partnershipData?.partnership?.hidePlanChangePage}
        />
        <SignInButtonSection
          onClick={onSignInClick}
          isUcl={Boolean(user?.id)}
          brand={brand}
          showConvenienceClaimSource={
            !isTempo && isAdSource && !hasKrogerParam && (!partnershipData || !partnershipData.ok)
          }
          containerClassName="pt-4 text-center"
        />
        {isUsaa && !isTempo && <UsaaLegal />}
      </div>
    </>
  );
};

export default IntroContainer;
